import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUser, FaUtensils, FaStore, FaCalendarAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import '../pages/ReceiptPage.css';

const ReceiptView = ({ sale, previewMode = false, documentType = 'receipt' }) => {
    const { t, i18n } = useTranslation();
    const { language } = i18n;
    const [imageError, setImageError] = useState(false);

    const isQuote = documentType === 'quote';

    const formatCurrency = useCallback((num) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(num || 0);
    }, []);

    // Helper to determine text color (black or white) based on background brightness
    const getContrastingColor = (hexColor) => {
        if (!hexColor) return '#000000';
        const hex = hexColor.replace('#', '');
        if (hex.length !== 6) return '#000000'; // Fallback

        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Luminance formula (Rec. 601)
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    };

    const textColor = getContrastingColor(sale?.invoice_color || '#ffffff');
    const mutedColor = textColor === '#FFFFFF' ? '#e0e0e0' : '#666666'; // Adjust muted text too

    if (!sale) return null;

    // Normalizar items: cotizaciones usan `items`, ventas usan `products`
    const lineItems = isQuote ? (sale.items || []) : (sale.products || []);
    const documentNumber = isQuote ? sale.quote_number : sale.invoice_number;

    const totalQuantity = lineItems.reduce((acc, item) => acc + parseFloat(item.quantity || 0), 0);

    // Default options (all true/visible by default if not set)
    const options = sale.invoice_display_options || {
        show_client_info: true,
        show_business_info: true, // Assuming this stays true or controlled elsewhere
        show_delivery_date: true,
        show_payment_history: true,
        show_observations: true,
        show_notes: true // Alias for observations
    };

    return (
        <div className={`receipt-container ${previewMode ? 'preview-mode' : ''}`} style={{ backgroundColor: sale.invoice_color || '#fff', color: textColor }}>
            {/* Header */}
            <div className="receipt-header">
                {sale.logo_url && !imageError ? (
                    <img
                        src={sale.logo_url}
                        alt="Logo"
                        className="receipt-logo"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="receipt-logo-placeholder">
                        <FaUtensils size={40} />
                    </div>
                )}
                <div className="receipt-title" style={{ color: textColor }}>
                    {isQuote
                        ? `${t('quotesPage.quoteTitle', 'COTIZACIÓN')} #${documentNumber}`
                        : `${t('invoicePage.receiptTitle', 'RECIBO')} #${documentNumber}`
                    }
                </div>
                <div className="receipt-disclaimer" style={{ color: mutedColor }}>
                    {isQuote
                        ? t('quotesPage.quoteDisclaimer', 'Este documento es una cotización y no constituye una venta ni una factura.')
                        : t('invoicePage.notAnInvoice', 'Este documento no constituye una factura electrónica ni documento fiscal equivalente.')
                    }
                </div>
            </div>

            {/* Business Info */}
            <div className="receipt-section info-row">
                <div className="info-left" style={{ color: textColor }}>
                    <FaStore className="me-2" /> {sale.business_name || t('invoicePage.yourBusiness')}
                </div>
                <div className="info-right">
                    <div style={{ color: textColor }}>{sale.business_address}</div>
                    <div style={{ color: textColor }}>{sale.business_phone}</div>
                    {sale.fiscal_id && (
                        <div style={{ fontSize: '0.85rem', color: mutedColor, marginTop: '2px' }}>
                            {sale.fiscal_id_type ? `${sale.fiscal_id_type}: ` : ''}{sale.fiscal_id}
                        </div>
                    )}
                </div>
            </div>

            {/* Client Info */}
            {options.show_client_info !== false && (
                <div className="receipt-section info-row">
                    <div className="info-left" style={{ color: textColor }}>
                        <FaUser className="client-icon" />
                        <span>{sale.client_name}</span>
                    </div>
                    <div className="info-right">
                        {sale.client_address && <div style={{ color: textColor }}>{sale.client_address}</div>}
                        <div style={{ color: textColor }}>{sale.client_phone}</div>
                    </div>
                </div>
            )}

            {/* Delivery Info (recibo) o Vigencia (cotización) */}
            {isQuote ? (
                sale.valid_until && (
                    <div className="receipt-section info-row">
                        <div className="info-left" style={{ color: textColor }}>
                            <FaCalendarAlt className="me-2" /> {t('quotesPage.validUntil', 'Válida hasta')}
                        </div>
                        <div className="info-right" style={{ color: textColor }}>
                            {new Date(sale.valid_until).toLocaleDateString(language, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            })}
                        </div>
                    </div>
                )
            ) : (
                options.show_delivery_date !== false && sale.delivery_datetime && (
                    <div className="receipt-section info-row">
                        <div className="info-left" style={{ color: textColor }}>
                            <FaCalendarAlt className="me-2" /> {t('invoicePage.deliveryDate', 'Fecha de Entrega')}
                        </div>
                        <div className="info-right" style={{ color: textColor }}>
                            {new Date(sale.delivery_datetime).toLocaleDateString(language, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </div>
                    </div>
                )
            )}

            {/* Items List */}
            <div className="receipt-section">
                <div className="items-header" style={{ color: textColor, borderBottomColor: mutedColor }}>
                    {lineItems.length} {t('invoicePage.items', 'artículos')} ( {t('invoicePage.qty', 'Cant.')} : {totalQuantity} )
                </div>

                {lineItems.map((item, idx) => (
                    <div className="item-row" key={item.item_id || item.id || idx} style={{ borderBottomColor: mutedColor }}>
                        <div className="item-details" style={{ color: textColor }}>
                            {item.quantity}x {item.product_name}
                        </div>
                        <div className="item-price" style={{ color: textColor }}>
                            {formatCurrency(item.quantity * item.unit_price)}
                        </div>
                    </div>
                ))}
            </div>

            {/* Totals */}
            <div className="totals-section">
                <div className="total-row">
                    <span className="total-label" style={{ color: mutedColor }}>{t('invoicePage.table.subtotal', 'Subtotal')}:</span>
                    <span className="total-value" style={{ color: mutedColor }}>{formatCurrency(sale.total_amount)}</span>
                </div>

                <div className="total-row grand-total">
                    <span className="total-label" style={{ color: textColor }}>{t('invoicePage.table.total', 'Total')}:</span>
                    <span className="total-value" style={{ color: textColor }}>{formatCurrency(sale.total_amount)}</span>
                </div>

                {/* Payment History & Balance — solo en recibo (no cotización) */}
                {!isQuote && options.show_payment_history !== false && sale.payments && sale.payments.length > 0 && (
                    <div className="payment-history-section mt-3 pt-2 border-top">
                        <div className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>{t('invoicePage.paymentHistory', 'Historial de Pagos')}:</div>
                        {sale.payments.map((payment) => (
                            <div className="total-row" key={payment.payment_id || Math.random()} style={{ fontSize: '0.85rem', color: mutedColor }}>
                                <span className="total-label">
                                    {new Date(payment.payment_date).toLocaleDateString(language)} - {t(`salesPage.paymentMethods.${payment.payment_method}`, payment.payment_method)}:
                                </span>
                                <span className="total-value">{formatCurrency(payment.amount)}</span>
                            </div>
                        ))}
                    </div>
                )}

                {!isQuote && (
                    <div className="total-row mt-2 pt-2 border-top">
                        <span className="total-label fw-bold">{t('invoicePage.table.amountPaid', 'Pagado')}:</span>
                        <span className="total-value text-success fw-bold">{formatCurrency(sale.paid_amount)}</span>
                    </div>
                )}

                {!isQuote && (parseFloat(sale.total_amount) - parseFloat(sale.paid_amount)) > 0 && (
                    <div className="total-row">
                        <span className="total-label fw-bold text-danger">{t('invoicePage.table.balanceDue', 'Pendiente')}:</span>
                        <span className="total-value text-danger fw-bold">
                            {formatCurrency(parseFloat(sale.total_amount) - parseFloat(sale.paid_amount))}
                        </span>
                    </div>
                )}
            </div>

            {/* Observations / Notes */}
            {options.show_observations !== false && sale.observations && (
                <div className="receipt-section mt-4">
                    <div style={{ backgroundColor: textColor === '#FFFFFF' ? 'rgba(255,255,255,0.1)' : '#f9f9f9', padding: '10px', borderRadius: '5px', fontSize: '0.85rem', color: mutedColor, fontStyle: 'italic' }}>
                        <strong>{t('invoicePage.notes', 'Nota')}:</strong> {sale.observations}
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="receipt-footer">
                {/* QR Code */}
                {options.show_qr_code !== false && sale.qr_code_url && (
                    <div className="receipt-qr-code text-center mb-3">
                        <img
                            src={sale.qr_code_url}
                            alt="QR Code"
                            style={{ width: '120px', height: '120px', objectFit: 'contain' }}
                        />
                    </div>
                )}

                <div className="footer-message">
                    {isQuote
                        ? t('quotesPage.receiptFooter', 'Gracias por tu interés. Contáctanos para confirmar tu pedido.')
                        : (sale.invoice_footer || t('invoicePage.invoiceFooterDefault', '¡Gracias por tu compra! 🤝'))
                    }
                </div>
                <div className="footer-date">
                    {isQuote ? (
                        <>
                            <div>
                                {t('quotesPage.receiptIssuedOn', 'Cotización emitida el')}{' '}
                                {new Date(sale.created_at).toLocaleDateString(language, {
                                    year: 'numeric', month: 'long', day: 'numeric',
                                })}
                            </div>
                            {sale.valid_until && (
                                <div className="fw-bold mt-1">
                                    {t('quotesPage.receiptValidUntil', 'Válida hasta el')}{' '}
                                    {new Date(sale.valid_until).toLocaleDateString(language, {
                                        year: 'numeric', month: 'long', day: 'numeric',
                                    })}
                                </div>
                            )}
                        </>
                    ) : (
                        new Date(sale.created_at).toLocaleDateString(language, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })
                    )}
                </div>
                {!previewMode && (
                    <div className="powered-by d-print-none">
                        {t('invoicePage.poweredBy')} <Link to="/">{t('invoicePage.appName')}</Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReceiptView;
